import React from 'react';
import { Link } from 'react-router-dom';
import { Share2, Newspaper, Eye } from 'lucide-react';
import OrbitHeader from '../OrbitHeader';
import { getAnimationCSS, getAvatarShape } from '../../utils/customisation';

/* ─── Avatar (round / orbit / full-width square) + name, bio, share row ─── */
export const ProfileHeader = ({ profile, profilePicUrl, ts, accent, anim, isLeft, username, hasDiaryPosts, handleShare }) => {
  return (
    <>
        {profile.orbit_enabled && profile.is_pro ? (
          <div className={`${isLeft ? 'text-left' : 'text-center'} mb-6`} style={getAnimationCSS(anim, 0)}>
            <OrbitHeader profile={profile} profilePicUrl={profilePicUrl} ts={ts} />
            <h1 className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: ts['--theme-text'] }} data-testid="profile-name">
              {profile.name}
            </h1>
            <p className="text-sm mb-3" style={{ color: ts['--theme-text-muted'] }} data-testid="profile-username">@{profile.username}</p>
            {profile.bio && (
              <p className={`text-sm mb-4 ${isLeft ? '' : 'max-w-sm mx-auto'} leading-relaxed`} style={{ color: ts['--theme-text'] }} data-testid="profile-bio">
                {profile.bio}
              </p>
            )}
            <div className={`flex gap-2 ${isLeft ? 'justify-start' : 'justify-center'} flex-wrap`}>
              <button onClick={handleShare} className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all hover:opacity-80"
                style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}40` }}
                data-testid="share-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share Profile
              </button>
              {hasDiaryPosts && (
                <Link to={`/${username}/fanpage`} className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all hover:opacity-80"
                  style={{ background: `${ts['--theme-card']}`, color: ts['--theme-text'], border: `1px solid ${ts['--theme-card-border']}` }}
                  data-testid="fan-page-btn">
                  <Newspaper className="w-4 h-4 mr-2" /> Fan Page
                </Link>
              )}
              {profile.total_views > 0 && (
                <span className="inline-flex items-center px-3 py-2 rounded-full text-xs font-medium"
                  style={{ background: `${ts['--theme-card']}`, color: ts['--theme-text-muted'], border: `1px solid ${ts['--theme-card-border']}` }}
                  data-testid="view-counter">
                  <Eye className="w-3 h-3 mr-1.5" />
                  {profile.total_views >= 1000 ? `${(profile.total_views / 1000).toFixed(1)}K` : profile.total_views} {profile.total_views === 1 ? 'view' : 'views'}
                </span>
              )}
            </div>
          </div>
        ) : (
        <div className={`${isLeft ? 'text-left' : 'text-center'} mb-8`} style={getAnimationCSS(anim, 0)}>
          {(() => {
            const shape = getAvatarShape(profile.avatar_shape);
            // FULL-WIDTH SQUARE: render the profile picture as a full-bleed
            // square at the very top with a theme-colour gradient feathering
            // into the page background. Pro-only — gated server-side already.
            if (shape.fullWidth && profilePicUrl) {
              const bg = ts['--theme-bg'] || '#0a0a0a';
              return (
                <div
                  className={`relative -mx-4 -mt-4 mb-6 overflow-hidden`}
                  style={{ aspectRatio: '1 / 1', maxHeight: 520 }}
                  data-testid="profile-fullwidth-square"
                >
                  <img
                    src={profilePicUrl}
                    alt={profile.name}
                    className="w-full h-full object-cover"
                    data-testid="profile-picture"
                  />
                  {/* Edge vignette — soft theme-coloured fade inward from all
                      four sides so the image blends into the page background
                      instead of slamming against a hard rectangle edge. */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ boxShadow: `inset 0 0 80px 24px ${bg}` }}
                    aria-hidden="true"
                  />
                  {/* Stronger bottom feather — keeps name/bio readable against
                      the page background below the image. */}
                  <div
                    className="absolute inset-x-0 bottom-0 pointer-events-none"
                    style={{
                      height: '45%',
                      background: `linear-gradient(to bottom, transparent 0%, ${bg} 95%)`,
                    }}
                    aria-hidden="true"
                  />
                </div>
              );
            }
            const avatarStyle = {
              border: `4px solid ${ts['--theme-card-border']}`,
              borderRadius: shape.borderRadius,
              ...(shape.clipPath ? { clipPath: shape.clipPath } : {}),
            };
            if (profilePicUrl) {
              return (
                <img src={profilePicUrl} alt={profile.name}
                  className={`w-28 h-28 object-cover ${isLeft ? '' : 'mx-auto'} mb-4 shadow-xl`}
                  style={avatarStyle}
                  data-testid="profile-picture" />
              );
            }
            return (
              <div className={`inline-flex items-center justify-center w-28 h-28 text-4xl font-bold ${isLeft ? '' : 'mx-auto'} mb-4 shadow-xl`}
                style={{ background: accent, color: ts['--theme-accent-text'], ...avatarStyle }}
                data-testid="profile-avatar">
                {profile.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            );
          })()}

          <h1 className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: ts['--theme-text'] }} data-testid="profile-name">
            {profile.name}
          </h1>
          <p className="text-sm mb-3" style={{ color: ts['--theme-text-muted'] }} data-testid="profile-username">@{profile.username}</p>
          {profile.bio && (
            <p className={`text-sm mb-4 ${isLeft ? '' : 'max-w-sm mx-auto'} leading-relaxed`} style={{ color: ts['--theme-text'] }} data-testid="profile-bio">
              {profile.bio}
            </p>
          )}

          <div className={`flex gap-2 ${isLeft ? 'justify-start' : 'justify-center'} flex-wrap`}>
            <button onClick={handleShare} className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all hover:opacity-80"
              style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}40` }}
              data-testid="share-btn">
              <Share2 className="w-4 h-4 mr-2" /> Share Profile
            </button>

            {hasDiaryPosts && (
              <Link to={`/${username}/fanpage`} className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all hover:opacity-80"
                style={{ background: `${ts['--theme-card']}`, color: ts['--theme-text'], border: `1px solid ${ts['--theme-card-border']}` }}
                data-testid="fan-page-btn">
                <Newspaper className="w-4 h-4 mr-2" /> Fan Page
              </Link>
            )}

            {profile.total_views > 0 && (
              <span className="inline-flex items-center px-3 py-2 rounded-full text-xs font-medium"
                style={{ background: `${ts['--theme-card']}`, color: ts['--theme-text-muted'], border: `1px solid ${ts['--theme-card-border']}` }}
                data-testid="view-counter">
                <Eye className="w-3 h-3 mr-1.5" />
                {profile.total_views >= 1000 ? `${(profile.total_views / 1000).toFixed(1)}K` : profile.total_views} {profile.total_views === 1 ? 'view' : 'views'}
              </span>
            )}
          </div>
        </div>
        )}
    </>
  );
};
